import React, { useState, useEffect, useCallback } from 'react'
import { Container, Button, Alert, Tabs, Tab, Modal } from 'react-bootstrap'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import Select from 'react-select'
import { amazonAPI } from '../api/amazonAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { usePermissions } from '../contexts/PermissionContext'
import AmazonAnomalySettingsPanel from '../components/AmazonAnomalySettingsPanel'
import AmazonProductExceptionsPanel from '../components/AmazonProductExceptionsPanel'
import AmazonBiddingStatusPanel from '../components/AmazonBiddingStatusPanel'
import AmazonAutomationSchedulePanel from '../components/AmazonAutomationSchedulePanel'
import '../styles/selectControl.css'

export const AD_AUTOMATION_SETTINGS_PATH = '/amazon-ad-automation/settings'
const HUB_PATH = '/amazon-ad-automation'
const HUB_LABEL = 'Back to Ad Automation'
// Ordered by how often they are used
const TABS = [
    { key: 'anomaly', title: 'Anomaly Detection' },
    { key: 'products', title: 'Product exceptions' },
    { key: 'bidding', title: 'Inventory-Aware Bidding' },
    { key: 'general', title: 'General' },
]
const TAB_KEYS = TABS.map(t => t.key)

/**
 * One home for every Ad Automation setting. Each tab says which automation it belongs to and at
 * what level (account, product, campaign). Tab, account and the product search live in the URL.
 */
function AmazonAdAutomationSettingsScreen() {
    const navigate = useNavigate()
    const location = useLocation()
    const { hasPermission } = usePermissions()
    const canManage = hasPermission('amazon.manage')
    const params = new URLSearchParams(location.search)
    const tab = TAB_KEYS.includes(params.get('tab')) ? params.get('tab') : 'anomaly'
    const accountId = params.get('account') || ''
    const search = params.get('search') || ''
    const [backTo] = useState(location.state?.backTo || HUB_PATH)
    const [backLabel] = useState(location.state?.backLabel || HUB_LABEL)

    const [accounts, setAccounts] = useState([])
    const [error, setError] = useState('')
    const [dirtyTabs, setDirtyTabs] = useState({})
    const [pendingChange, setPendingChange] = useState(null)
    const anyDirty = Object.values(dirtyTabs).some(Boolean)

    const setUrl = useCallback((changes) => {
        const next = new URLSearchParams(location.search)
        Object.entries(changes).forEach(([key, value]) => {
            if (value) next.set(key, value)
            else next.delete(key)
        })
        navigate(`${location.pathname}?${next.toString()}`, { replace: true, state: location.state })
    }, [location.pathname, location.search, location.state, navigate])

    useEffect(() => {
        amazonAPI.accounts.getAll()
            .then(data => {
                const list = Array.isArray(data) ? data : (data?.results || [])
                setAccounts(list)
                if (!accountId && list.length) setUrl({ account: String(list[0].id) })
            })
            .catch(err => setError(formatErrorResponse(err)))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Warn before leaving the page (reload, close) with unsaved edits
    useEffect(() => {
        if (!anyDirty) return undefined
        const warn = (e) => { e.preventDefault(); e.returnValue = '' }
        window.addEventListener('beforeunload', warn)
        return () => window.removeEventListener('beforeunload', warn)
    }, [anyDirty])

    const markDirty = useCallback((tabKey) => (dirty) => {
        setDirtyTabs(d => (d[tabKey] === dirty ? d : { ...d, [tabKey]: dirty }))
    }, [])
    const dirtyHandlers = React.useMemo(
        () => Object.fromEntries(TAB_KEYS.map(k => [k, markDirty(k)])), [markDirty],
    )

    // Switching tab or account drops the current tab's draft, so ask first
    const requestChange = (changes) => {
        if (dirtyTabs[tab]) setPendingChange(changes)
        else setUrl(changes)
    }
    const confirmDiscard = () => {
        setDirtyTabs(d => ({ ...d, [tab]: false }))
        setUrl(pendingChange)
        setPendingChange(null)
    }

    const accountOptions = accounts.map(a => ({ value: String(a.id), label: a.account_name }))
    const accountName = accounts.find(a => String(a.id) === accountId)?.account_name || ''
    const selfPath = `${location.pathname}${location.search}`

    return (
        <Container fluid className="px-4 py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1 className="mb-0">Ad Automation Settings</h1>
                <Button variant="outline-primary" onClick={() => navigate(backTo)}>
                    <i className="fas fa-arrow-left me-2"></i>{backLabel}
                </Button>
            </div>

            {!canManage && (
                <Alert variant="info">You can view these settings but not change them.</Alert>
            )}
            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span className="text-body-secondary">Account</span>
                <div style={{ minWidth: '240px' }}>
                    <Select
                        value={accountOptions.find(opt => opt.value === accountId) || null}
                        onChange={(opt) => requestChange({ account: opt?.value || '', search: '' })}
                        options={accountOptions}
                        placeholder="Select Account..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </div>
            </div>

            <Tabs
                activeKey={tab}
                onSelect={(key) => key !== tab && requestChange({ tab: key, search: key === 'products' ? search : '' })}
                className="mb-4"
                mountOnEnter
                unmountOnExit
            >
                {TABS.map(t => (
                    <Tab
                        key={t.key}
                        eventKey={t.key}
                        title={<span>{t.title}{dirtyTabs[t.key] && <span className="text-warning ms-1" title="Unsaved changes">●</span>}</span>}
                    >
                        {accountId && t.key === 'anomaly' && (
                            <AmazonAnomalySettingsPanel key={accountId} accountId={accountId} canManage={canManage}
                                onDirtyChange={dirtyHandlers.anomaly} />
                        )}
                        {accountId && t.key === 'products' && (
                            <AmazonProductExceptionsPanel key={accountId} accountId={accountId} canManage={canManage}
                                search={search} onSearchChange={(value) => setUrl({ search: value })} />
                        )}
                        {accountId && t.key === 'bidding' && (
                            <AmazonBiddingStatusPanel key={accountId} accountId={accountId} accountName={accountName}
                                backTo={selfPath} />
                        )}
                        {accountId && t.key === 'general' && (
                            <AmazonAutomationSchedulePanel key={accountId} accountId={accountId} canManage={canManage}
                                onDirtyChange={dirtyHandlers.general} />
                        )}
                    </Tab>
                ))}
            </Tabs>

            <Modal show={!!pendingChange} onHide={() => setPendingChange(null)} backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Discard changes?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    The {TABS.find(t => t.key === tab)?.title} tab has changes that are not saved.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={() => setPendingChange(null)}>Keep editing</Button>
                    <Button variant="danger" onClick={confirmDiscard}>Discard</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    )
}

/** Old Anomaly Settings / Product Alert Settings links: open the matching tab, keeping the filters. */
export function AmazonAdAutomationSettingsRedirect({ tab }) {
    const location = useLocation()
    const params = new URLSearchParams(location.search)
    params.set('tab', tab)
    return <Navigate to={`${AD_AUTOMATION_SETTINGS_PATH}?${params.toString()}`} replace state={location.state} />
}

export default AmazonAdAutomationSettingsScreen
